import React, { useState, useMemo, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import PropTypes from 'prop-types';
import CommitPieCustomTooltip from './CommitPieCustomTooltip';
import PRPieCustomTooltip from './PRPieCustomTooltip';
// Generate a random color from HSL for better distinction
const generateColor = (i, total) => {
    const hue = (i * (360 / total)) % 360;
    return `hsl(${hue}, 65%, 55%)`;
};
const CustomLegend = ({ labels, colorMap, activeKeys, toggleKey }) => (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
        <ul style={{
            listStyle: 'none',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '16px',
            padding: 0,
            margin: 0,
            maxWidth: '90%', // optional: prevents stretching too wide
        }}>
            {labels.map((label) => {
                const isActive = activeKeys.includes(label);
                const color = colorMap[label];
                return (
                    <li key={label}>
                        <button
                            onClick={() => toggleKey(label)}
                            style={{
                                display: 'flex',
                                type: 'line',
                                alignItems: 'center',
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                color: isActive ? color : '#ccc',
                                fontWeight: 'normal',
                                cursor: 'pointer'
                            }}
                        >
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    backgroundColor: isActive ? color : '#ccc',
                                    marginRight: 6,
                                }}
                            ></span>
                            {label}
                        </button>
                    </li>
                );
            })}
        </ul>
    </div>
);
const SharePieChart = ({ data }) => {
    const commitsDataRaw = data.filter(d => d.totalCommits > 0);
    const prDataRaw = data
        .filter(d => (d.totalMergedPrs + d.totalOpenPrs + d.totalDeclinedPrs) > 0)
        .map(d => ({
            name: d.developer,
            developer: d.developer,
            value: d.totalMergedPrs + d.totalOpenPrs + d.totalDeclinedPrs,
            mergedPRs: d.totalMergedPrs,
            openPRs: d.totalOpenPrs,
            declinedPRs: d.totalDeclinedPrs
        }));
    const developerLabels = useMemo(() => {
        const commits = data.filter(d => d.totalCommits > 0);
        const prs = data
            .filter(d => (d.totalMergedPrs + d.totalOpenPrs + d.totalDeclinedPrs) > 0)
            .map(d => d.developer);
        return Array.from(new Set([...commits.map(d => d.developer), ...prs]));
    }, [data]);
    const [activeDevs, setActiveDevs] = useState([]);
    // Update activeDevs when developerLabels change
    useEffect(() => {
        setActiveDevs(developerLabels);
    }, [developerLabels]);
    // Dynamically generate a color map per developer
    const colorMap = useMemo(() => {
        const map = {};
        developerLabels.forEach((dev, i) => {
            map[dev] = generateColor(i, developerLabels.length);
        });
        return map;
    }, [developerLabels]);
    const toggleDev = (name) => {
        setActiveDevs(prev =>
            prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
        );
    };
    const commitsData = commitsDataRaw
        .filter(d => activeDevs.includes(d.developer))
        .map(d => ({
            name: d.developer,
            developer: d.developer,
            value: d.totalCommits
        }));
    const prData = prDataRaw
        .filter(d => activeDevs.includes(d.developer));
    return (
        <div style={{ textAlign: 'center' }}>

            <CustomLegend
                labels={developerLabels}
                colorMap={colorMap}
                activeKeys={activeDevs}
                toggleKey={toggleDev}
            />
            <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: '20px' }}>
                {/* Commits Pie */}
                <div>
                    <h4>Commits</h4>
                    {commitsData.length > 0 ? (
                        <PieChart width={450} height={450}>
                            <Pie
                                data={commitsData}
                                cx="50%"
                                cy="50%"
                                outerRadius={160}
                                dataKey="value"
                                nameKey="name"
                                label
                            >
                                {commitsData.map((entry) => (
                                    <Cell key={`commit-${entry.name}`} fill={colorMap[entry.name]} />
                                ))}
                            </Pie>
                            <Tooltip content={<CommitPieCustomTooltip />} />
                        </PieChart>
                    ) : (
                        <div style={{width:450 , height: 450, display: 'flex', alignItems: 'center' , justifyContent: 'center'}}>
                            <p>No data Available</p>
                        </div>
                    )}
                </div>
                {/* PRs Pie */}
                <div>
                    <h4>Pull Requests</h4>
                    {prData.length > 0 ? (
                        <PieChart width={450} height={450}>
                            <Pie
                                data={prData}
                                cx="50%"
                                cy="50%"
                                outerRadius={160}
                                dataKey="value"
                                nameKey="name"
                                label
                            >
                                {prData.map((entry) => (
                                    <Cell key={`pr-${entry.name}`} fill={colorMap[entry.name]} />
                                ))}
                            </Pie>
                            <Tooltip content={<PRPieCustomTooltip />} />
                        </PieChart>
                    ) : (
                        <div style={{width:450 , height: 450, display: 'flex', alignItems: 'center' , justifyContent: 'center'}}>
                            <p>No data Available</p>
                        </div>
                    )}
                </div>
            </div>
            {/* Shared interactive legend */}

        </div>
    );
};
SharePieChart.propTypes = {
    data: PropTypes.array.isRequired
};

CustomLegend.propTypes = {
    labels: PropTypes.array.isRequired,
    colorMap: PropTypes.array.isRequired,
    activeKeys: PropTypes.array.isRequired,
    toggleKey: PropTypes.array.isRequired
};
export default SharePieChart;